'use client'

import { usePathname, useSearchParams } from '@/utils/navigation'
import useRouter from '@/utils/Router'
import React, { FunctionComponent, memo, useEffect, useMemo, useState } from 'react'
import { useSnapshot } from 'valtio'
import store from '@/store'
import isPlatformAdmin, { PLATFORM_ROUTE } from '@/utils/platformAdmin'


interface NavigationChipsProps {
}

const navigationExpandedStorageKey = 'main-navigation-expanded'
const expandedNavigationBreakpoint = 1200

const getInitialExpandedState = () => {
    if (typeof window === 'undefined') return true

    try {
        const savedState = localStorage.getItem(navigationExpandedStorageKey)

        if (savedState === 'true') return true
        if (savedState === 'false') return false
    } catch {
        // Fall back to the responsive default when storage is unavailable.
    }

    return window.innerWidth >= expandedNavigationBreakpoint
}

const baseOptions = [
    {
        name: 'Orders',
        icon: '',
        route: '/orders',
    },
    {
        name: 'Products',
        icon: '',
        route: '/products',
    },
    {
        name: 'Media',
        icon: "",
        route: '/media',
    },
    {
        name: 'Machine',
        icon: '',
        route: '/machines',
    },
    {
        name: 'Users',
        icon: '',
        route: '/users',
    },
    {
        name: 'Settings',
        icon: "",
        route: '/themes',
    },
    {
        // Neatolabs-internal, cross-company settings. Filtered out below for
        // everyone without the platform admin role.
        name: 'Platform',
        icon: '', // fa-wrench
        route: PLATFORM_ROUTE,
    },
    {
        name: 'Help',
        icon: '',
        route: '/help',
    },
]

const getRoutePathname = (route: string) => route.split(/[?#]/, 1)[0]

const NavigationChips: FunctionComponent<NavigationChipsProps> = () => {
    const [selectedOption, setSelectedOption] = useState(0)
    const [expanded, setExpanded] = useState(getInitialExpandedState)
    const [position, setPosition] = useState([6, 37, 37])
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const { companySetting, currentUser } = useSnapshot(store)

    const canSeePlatform = isPlatformAdmin(currentUser)

    const filteredOptions = useMemo(() => {
        const isNeatolabs = companySetting.machine === 'neatolabs'
        return baseOptions.filter(option => {
            if (option.route === '/machines' && !isNeatolabs) return false
            if (option.route === PLATFORM_ROUTE && !canSeePlatform) return false
            return true
        })
    }, [companySetting.machine, canSeePlatform])


    const activeOptionIndex = useMemo(() => {
        return filteredOptions.findIndex(option => {
            const optionPathname = getRoutePathname(option.route)
            return optionPathname === pathname || pathname.startsWith(`${optionPathname}/`)
        })
    }, [filteredOptions, pathname])
    const showNavigation = useMemo(() => (
        filteredOptions.some(option => getRoutePathname(option.route) === pathname)
    ), [filteredOptions, pathname])

    useEffect(() => {

        if (!searchParams.has('storeName')) {
            const queryString = searchParams.toString()
            router.push(queryString ? `${pathname}?${queryString}` : pathname)
        }
        if (activeOptionIndex !== -1) {
            setSelectedOption(activeOptionIndex)
            setPosition([
                activeOptionIndex * 37 + 6,
                37,
                37,
            ])
        }
    }, [pathname, activeOptionIndex])

    const handleMouseDown = (index: number) => {
        const c = 0
        const top = index > selectedOption
            ? selectedOption * 37 + (6 + c / 2)
            : index * 37 + (6 + c / 2)

        setPosition([
            top,
            (Math.abs(selectedOption - index) * 37) + 37,
            37 - c,
        ])
    }

    const handleSelect = (index: number, route: string) => {
        window.setTimeout(() => {
            setPosition([
                index * 37 + 6,
                37,
                37,
            ])
            setSelectedOption(index)
            router.push(route)
        }, 250)
    }

    const handleToggleExpanded = () => {
        setExpanded((currentState) => {
            const nextState = !currentState

            try {
                localStorage.setItem(navigationExpandedStorageKey, String(nextState))
            } catch {
                // Keep the in-memory state working when storage is unavailable.
            }

            return nextState
        })
    }

    if (!showNavigation) {
        return null
    }

    return (
        <div className="sticky top-0 flex pl-[32px] py-[26px] select-none mr-[32px]">
            <div className={`!sticky !top-[101px] ${expanded ? 'w-[200px]' : 'w-[50px]'} relative h-fit overflow-hidden transition-all duration-300`}>
                <div className="absolute w-[49px] h-full bg-gray-100 z-[1] rounded-full top-0 left-0" />
                <div
                    className="absolute w-[37px] h-[37px] bg-gray-900 z-[1] rounded-full transition-all duration-300"
                    style={{
                        top: `${position[0]}px`,
                        height: `${position[1]}px`,
                        width: `${position[2]}px`,
                        left: `${(49 - position[2]) / 2}px`,
                    }}
                />
                <ul className="relative z-[2] p-[6px] w-max">
                    {filteredOptions.map((option, index) => (
                        <li
                            key={option.name}
                            onMouseDown={() => handleMouseDown(index)}
                            onClick={() => handleSelect(index, option.route)}
                            className="group flex flex-row gap-[27px] h-[37px] items-center text-md text-gray-500 font-normal hover:text-gray-900 cursor-pointer transition-all"
                        >
                            <span
                                className={`duration-100 font-icon-regular w-[37px] h-[37px] flex items-center justify-center text-[16px] font-normal ${
                                    index === selectedOption && 'text-white hover:text-white'
                                }`}
                            >
                                {option.icon}
                            </span>
                            <span
                                className={`${!expanded ? 'opacity-0' : ''} flex min-w-[126px] items-center justify-between gap-sm pr-sm transition-all duration-200 text-sm whitespace-nowrap ${
                                    index === selectedOption && 'text-gray-900 font-semibold'
                                }`}
                            >
                                <span>{option.name}</span>
                            </span>
                        </li>
                    ))}
                    <li
                        className={`${!expanded ? 'rotate-180' : 'rotate-0'} bg-white w-[37px] h-[37px] cursor-pointer text-gray-500 rounded-full flex justify-center items-center mt-md hover:bg-gray-300 transition-all font-icon-regular`}
                        onClick={handleToggleExpanded}
                    >
                        
                    </li>
                </ul>
            </div>
        </div>
    )
}

export default memo(NavigationChips)
